"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, DayPickerProps } from "react-day-picker";

import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: DayPickerProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col relative",
        month: "space-y-2",
        month_caption: "flex justify-center relative items-center",
        caption_label:
          "text-sm font-medium relative inline-flex items-center p-1 gap-1",
        nav: "space-x-1 flex items-center absolute z-10 w-full justify-between",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100",
        ),
        range_end: "day-range-end",
        selected:
          "[&>button]:!text-primary-foreground [&>button]:!bg-primary [&>button]:hover:!bg-primary [&>button]:hover:!text-primary-foreground [&>button]:focus:!bg-primary [&>button]:focus:!text-primary-foreground",
        today: "[&>button]:bg-accent [&>button]:text-accent-foreground",
        outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        dropdowns: "flex flex-row",
        dropdown: "opacity-0 absolute flex flex-row z-10",
        week_number_header: "bg-slate-100",
        week_number:
          "text-muted-foreground text-sm font-normal bg-slate-100 px-2 border-r border-slate-200",
        weekday: "text-foreground text-sm font-bold py-2 bg-slate-100",
        ...classNames,
      }}
      components={{
        PreviousMonthButton: ({ className, ...props }) => (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
              className,
            )}
            {...props}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        ),
        NextMonthButton: ({ className, ...props }) => (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
              className,
            )}
            {...props}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
